import { type SetStateAction, type Dispatch, useState } from 'react'
import { Flex } from '@/components/priority/Flex/Flex'
import { Check } from '@/components/global/Check/Check'
import { Button } from '@/components/priority/Button/Button'
import { PdfViewerProps } from '@/components/pdfRender/PdfViewer'
import { useSetToast } from '@/hooks/utility/useSetToast'
import { useTranslation } from 'react-i18next'
import {
  EDIT_TOKEN_ROLE,
  EDIT_TOKEN_STORAGE_KEYS,
  LEGACY_QC_SHADOW_ACCESS,
  PDF_STATUS_KEY,
  STATUS_KEY,
} from '@messenger/consts'
import { PdfStatusKey, ApplicationType } from '@messenger/types'
import { useRecoilValue } from 'recoil'
import { caseAtom } from '@/atoms/caseAtoms'
import type { Case } from '@/types/case'
import type { ProofGalleryDetails } from '@/hooks/pdf/usePdfViewer'
import { leaveForReturnUrl } from '@/utils/editor/returnToOrderUrl'

type PdfViewerFooterProps = Pick<
  PdfViewerProps,
  'handleFinalizePdf' | 'productStatus'
> & {
  disclaimerAcknowledged: boolean
  setDisclaimerAcknowledged: Dispatch<SetStateAction<boolean>>
  pdfUrl: string
  pdfGenerationStatus: PdfStatusKey
  applicationType?: ApplicationType
  proofGalleryDetails?: ProofGalleryDetails | null
}

export const PdfViewerFooter = ({
  disclaimerAcknowledged,
  handleFinalizePdf,
  setDisclaimerAcknowledged,
  pdfGenerationStatus,
  pdfUrl,
  productStatus,
  applicationType,
  proofGalleryDetails,
}: PdfViewerFooterProps) => {
  const { t, i18n } = useTranslation()
  const setToast = useSetToast()
  const [isFinalizing, setIsFinalizing] = useState(false)
  const caseData = useRecoilValue(caseAtom) as Case
  const isM3 = applicationType === 'M3'
  const isClosed = productStatus === STATUS_KEY.CLOSED

  // A QC session: the shared QC token's role, or a legacy qc_shadow link.
  const isQcShadowAccess =
    typeof window !== 'undefined' &&
    (sessionStorage.getItem(EDIT_TOKEN_STORAGE_KEYS.role) ===
      EDIT_TOKEN_ROLE.QC ||
      new URLSearchParams(window.location.search).get('access') ===
        LEGACY_QC_SHADOW_ACCESS)
  const hasProofScreenshots =
    (proofGalleryDetails?.screenshotUrls?.length ?? 0) > 0
  const hasViewableProof = !!pdfUrl || hasProofScreenshots
  const checkDisabled =
    !hasViewableProof || pdfGenerationStatus !== PDF_STATUS_KEY.COMPLETE
  const checkColor = !checkDisabled ? 'almostBlack' : 'gray'
  const checkOnClick = !checkDisabled
    ? () => setDisclaimerAcknowledged(!disclaimerAcknowledged)
    : () => {}

  const handleFinalize = async () => {
    if (!disclaimerAcknowledged) {
      setToast({
        icon: 'info',
        text: t('pdf.colorDisclaimerAcknowledgmentToast'),
      })
      return
    }

    if (isClosed) {
      setToast({
        icon: 'info',
        text: t('pdf.closedToast'),
      })
      return
    }

    // For M3, navigate to the return URL (unless QC shadow access)
    if (isM3 && caseData?.return_url && !isQcShadowAccess) {
      leaveForReturnUrl(caseData.return_url, i18n.language)
      return
    }

    if (handleFinalizePdf) {
      setIsFinalizing(true)
      try {
        await handleFinalizePdf()
      } catch (error) {
        console.error('Finalization failed:', error)
      }
      setIsFinalizing(false)
    }
  }

  const finalizeButtonText = () => {
    if (isFinalizing) return t('pdf.finalizing')
    // Show "Return to Order" for normal M3 users, "Finalize" for QC shadow access
    if (isM3 && !isQcShadowAccess) return t('pdf.returnToOrder')
    return t('pdf.finalize')
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      width="100%"
      padding="24px"
      role="region"
      aria-label={t('pdf.pdfFooterRegionAriaLabel')}
    >
      <Flex flex="1" />

      <Flex align="center" gap="12px" justify="center" flex="1">
        <Check
          disabled={checkDisabled}
          checked={disclaimerAcknowledged}
          text={t('pdf.colorDisclaimerAcknowledgment')}
          color={checkColor}
          icon="check"
          onClick={checkOnClick}
        />
      </Flex>

      <Flex justify="flex-end" flex="1">
        <Button
          disabled={!disclaimerAcknowledged}
          onClick={handleFinalize}
          primary
          aria-label={
            isFinalizing
              ? t('pdf.finalizingButtonAriaLabel')
              : t('pdf.finalizeButtonAriaLabel')
          }
        >
          {finalizeButtonText()}
        </Button>
      </Flex>
    </Flex>
  )
}
